import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, LabelList, ReferenceLine,
} from 'recharts';
import { createChart, ColorType, CrosshairMode, LineStyle } from 'lightweight-charts';
import {
  PRECIO_OFICIAL, PALETTE, SPANISH_MONTHS_SHORT, SPANISH_MONTHS_LONG,
  fmtCurrency, fmtDollars, fmtPct, titleCase,
  formatRelativeTime, freshnessLevel, summarizeFreshness,
} from './chartShared';

const REFRESH_MS = 5 * 60 * 1000;
import './WeeklyAverageYearChart.css';
import './PriceRangeChart.css';

// Custom tooltip styled like the rest of the chart family
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const value = payload[0].value;
  const diff  = value - PRECIO_OFICIAL;
  return (
    <div className="prc-tip prc-tip--static">
      <div className="prc-tip__date">{label}</div>
      <div className="prc-tip__price">{fmtCurrency(value)}</div>
      <div className="prc-tip__diff" style={{ color: diff >= 0 ? PALETTE.green : PALETTE.red }}>
        {diff >= 0
          ? `${fmtDollars(diff)} sobre el oficial`
          : `${fmtDollars(diff)} bajo el oficial`}
      </div>
    </div>
  );
};

const MonthlySummary = () => {
  const { t } = useTranslation();
  const [zone, setZone] = useState('norte');
  const [monthlyData, setMonthlyData] = useState(null);
  const [history, setHistory] = useState([]);
  const [rangeRaw, setRangeRaw] = useState([]); // raw reports for freshness
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, setNowTick] = useState(0);

  const histChartContainerRef = useRef(null);
  const histTooltipRef        = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const run = async (silent = false) => {
      const token = localStorage.getItem('access_token');
      if (!token) { setError(t('auth_required')); setLoading(false); return; }
      if (!silent) setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/prices/monthly-summary/?zone=${zone}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        setMonthlyData(response.data);
        setError(null);
      } catch (err) {
        if (!silent) setError(t('monthly_error'));
      } finally {
        if (!silent && !cancelled) setLoading(false);
      }
    };
    run();
    const interval = setInterval(() => run(true), REFRESH_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [t, zone]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(n => n + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Fetch 12-month history → aggregate by year-month + capture raw for freshness
  useEffect(() => {
    const fetch12 = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      try {
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth() - 11, 1);
        const startISO = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-01`;
        const endISO   = today.toISOString().slice(0, 10);
        const r = await axios.get(
          `${process.env.REACT_APP_API_URL}/prices/range/?zone=${zone}&start_date=${startISO}&end_date=${endISO}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setRangeRaw(r.data || []);
        const monthMap = new Map();
        for (const p of r.data || []) {
          const yymm = String(p.date).slice(0, 7); // YYYY-MM
          const v = parseFloat(p.price_per_box);
          if (!yymm.match(/^\d{4}-\d{2}$/) || Number.isNaN(v)) continue;
          if (!monthMap.has(yymm)) monthMap.set(yymm, []);
          monthMap.get(yymm).push(v);
        }
        const months = [...monthMap.entries()]
          .map(([key, values]) => {
            const [y, m] = key.split('-').map(Number);
            return {
              time: { year: y, month: m, day: 15 },
              value: values.reduce((s, v) => s + v, 0) / values.length,
              count: values.length,
              key,
            };
          })
          .sort((a, b) => a.key.localeCompare(b.key));
        setHistory(months);
      } catch (err) {
        console.error('Error fetching 12mo history:', err);
        setHistory([]);
      }
    };
    fetch12();
  }, [zone]);

  const stats = useMemo(() => {
    if (!monthlyData) return null;
    const avg = parseFloat(monthlyData.avg_price_per_box);
    const min = parseFloat(monthlyData.min_price_per_box);
    const max = parseFloat(monthlyData.max_price_per_box);
    const avgKg = parseFloat(monthlyData.avg_price_per_kg);
    const diffOficial    = avg - PRECIO_OFICIAL;
    const diffOficialPct = (diffOficial / PRECIO_OFICIAL) * 100;
    return { avg, min, max, avgKg, diffOficial, diffOficialPct };
  }, [monthlyData]);

  const chartData = stats ? [
    { name: 'Mínimo',   value: stats.min, color: PALETTE.red,    accent: 'min' },
    { name: 'Promedio', value: stats.avg, color: PALETTE.yellow, accent: 'avg' },
    { name: 'Máximo',   value: stats.max, color: PALETTE.green,  accent: 'max' },
  ] : [];

  const insight = stats && (() => {
    if (stats.diffOficial >= 0.30) {
      return { icon: '🌱', text: `Buen mes. El promedio quedó ${fmtDollars(stats.diffOficial)} sobre el precio oficial.` };
    }
    if (stats.diffOficial <= -0.30) {
      return { icon: '⚠️', text: `Mes flojo. El promedio quedó ${fmtDollars(stats.diffOficial)} bajo el precio oficial.` };
    }
    return { icon: '📊', text: `El promedio del mes quedó cerca del precio oficial (${fmtCurrency(PRECIO_OFICIAL)}).` };
  })();

  // Month-over-month delta from history
  const histStats = useMemo(() => {
    if (history.length < 2) return null;
    const last = history[history.length - 1];
    const prev = history[history.length - 2];
    const diff = last.value - prev.value;
    const pct  = prev.value !== 0 ? (diff / prev.value) * 100 : 0;
    return { last, prev, diff, pct };
  }, [history]);

  // Mini-chart (12 months) lifecycle
  useEffect(() => {
    if (!histChartContainerRef.current || history.length < 2) return undefined;

    const chart = createChart(histChartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor:  PALETTE.lightGray,
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        attributionLogo: false,
      },
      grid: { vertLines: { visible: false }, horzLines: { color: '#f0f1f5' } },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.2, bottom: 0.15 } },
      timeScale: {
        borderVisible: false,
        timeVisible: false,
        tickMarkFormatter: (time) => {
          if (time && typeof time === 'object' && 'year' in time) {
            return SPANISH_MONTHS_SHORT[time.month - 1] ?? '';
          }
          return '';
        },
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: { color: PALETTE.lightGray, width: 1, style: LineStyle.Dotted, labelVisible: false },
        horzLine: { color: PALETTE.lightGray, width: 1, style: LineStyle.Dotted, labelVisible: false },
      },
      handleScroll: false,
      handleScale: false,
    });

    const areaSeries = chart.addAreaSeries({
      lineColor:    PALETTE.green,
      lineWidth:    3,
      topColor:     'rgba(23,198,113,0.32)',
      bottomColor:  'rgba(23,198,113,0)',
      priceLineVisible: false,
      lastValueVisible: false,
      priceFormat:  { type: 'price', precision: 2, minMove: 0.01 },
    });
    areaSeries.setData(history.map(({ time, value }) => ({ time, value })));

    areaSeries.createPriceLine({
      price: PRECIO_OFICIAL,
      color: PALETTE.lightGray,
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      axisLabelVisible: true,
      title: 'Oficial',
    });

    // Best / worst month markers
    const sorted = [...history].sort((a, b) => a.value - b.value);
    const minM = sorted[0];
    const maxM = sorted[sorted.length - 1];
    if (minM && maxM && minM !== maxM) {
      areaSeries.setMarkers([
        { time: maxM.time, position: 'aboveBar', color: PALETTE.green, shape: 'arrowDown', text: `Mejor ${fmtCurrency(maxM.value)}` },
        { time: minM.time, position: 'belowBar', color: PALETTE.red,   shape: 'arrowUp',   text: `Peor ${fmtCurrency(minM.value)}` },
      ]);
    }

    chart.timeScale().fitContent();

    chart.subscribeCrosshairMove(param => {
      const tip = histTooltipRef.current;
      const container = histChartContainerRef.current;
      if (!tip || !container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (!param.time || !param.point || param.point.x < 0 || param.point.y < 0 || param.point.x > w || param.point.y > h) {
        tip.style.display = 'none';
        return;
      }
      const data = param.seriesData.get(areaSeries);
      if (!data) { tip.style.display = 'none'; return; }
      const tm = data.time;
      if (!(tm && typeof tm === 'object' && 'year' in tm)) { tip.style.display = 'none'; return; }
      const label = titleCase(`${SPANISH_MONTHS_LONG[tm.month - 1]} ${tm.year}`);
      const diff = data.value - PRECIO_OFICIAL;
      const diffLabel = diff >= 0
        ? `<span style="color:${PALETTE.green}">${fmtPct((diff / PRECIO_OFICIAL) * 100)} sobre el oficial</span>`
        : `<span style="color:${PALETTE.red}">${fmtPct((diff / PRECIO_OFICIAL) * 100)} bajo el oficial</span>`;
      tip.innerHTML = `
        <div class="prc-tip__date">${label}</div>
        <div class="prc-tip__price">${fmtCurrency(data.value)}</div>
        <div class="prc-tip__diff">${diffLabel}</div>
      `;
      tip.style.display = 'block';
      const tipW = tip.clientWidth;
      const tipH = tip.clientHeight;
      let x = param.point.x + 16;
      let y = param.point.y - tipH - 16;
      if (x + tipW > w) x = param.point.x - tipW - 16;
      if (y < 0) y = param.point.y + 16;
      tip.style.left = `${x}px`;
      tip.style.top  = `${y}px`;
    });

    const handleResize = () => {
      if (!histChartContainerRef.current) return;
      chart.applyOptions({ width: histChartContainerRef.current.clientWidth });
      chart.timeScale().fitContent();
    };
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
    };
  }, [history]);

  if (loading) return <div className="loading">{t('loading_monthly')}</div>;
  if (error)   return <div className="error">{error}</div>;
  if (!monthlyData || !stats) return <div className="loading">{t('no_data_available')}</div>;

  const freshness = summarizeFreshness(rangeRaw);
  const freshLevel = freshnessLevel(freshness.latest);

  return (
    <div className="monthly-summary-container">
      <div className="zone-selector-row">
        <h2>{t('monthly_summary')}</h2>
        <div className="zone-tabs">
          <button className={`zone-tab ${zone === 'norte' ? 'zone-tab--active' : ''}`} onClick={() => setZone('norte')}>{t('zona_norte')}</button>
          <button className={`zone-tab ${zone === 'sur'   ? 'zone-tab--active' : ''}`} onClick={() => setZone('sur')}>{t('zona_sur')}</button>
        </div>
      </div>

      <div className="prc-card">
        {freshness.latest && (
          <div className="prc-freshness">
            <span className={`prc-freshness__dot prc-freshness__dot--${freshLevel}`} />
            <span>
              <span className="prc-freshness__strong">Última actualización:</span> {formatRelativeTime(freshness.latest)}
              {freshness.todayCount > 0 && (
                <> · {freshness.todayCount} {freshness.todayCount === 1 ? 'reporte' : 'reportes'} hoy</>
              )}
            </span>
          </div>
        )}
        <div className="prc-hero">
          <div className="prc-hero__main">
            <div className="prc-hero__value">{fmtCurrency(stats.avg)}</div>
            <div className="prc-hero__caption">
              promedio de {monthlyData.month?.toLowerCase?.()} {monthlyData.year}
            </div>
          </div>
          <div className="prc-hero__deltas">
            <span className="prc-delta">
              Por kilo: <strong style={{ color: PALETTE.navy }}>{fmtCurrency(stats.avgKg, 3)}</strong>
            </span>
            <span className={`prc-delta ${stats.diffOficial >= 0 ? 'prc-delta--up' : 'prc-delta--down'}`}>
              {stats.diffOficial >= 0
                ? `${fmtPct(stats.diffOficialPct)} sobre el precio oficial 🌱`
                : `${fmtPct(stats.diffOficialPct)} bajo el precio oficial ⚠️`}
            </span>
          </div>
        </div>

        {/* Bars: min / avg / max with reference line at oficial */}
        <div className="prc-chart-wrap" style={{ marginTop: 8 }}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={chartData}
              margin={{ top: 30, right: 24, left: 0, bottom: 8 }}
              barCategoryGap="22%"
            >
              <defs>
                <linearGradient id="msGradMin" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={PALETTE.red}    stopOpacity={1} />
                  <stop offset="100%" stopColor={PALETTE.red}    stopOpacity={0.55} />
                </linearGradient>
                <linearGradient id="msGradAvg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={PALETTE.yellow} stopOpacity={1} />
                  <stop offset="100%" stopColor={PALETTE.yellow} stopOpacity={0.55} />
                </linearGradient>
                <linearGradient id="msGradMax" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={PALETTE.green}  stopOpacity={1} />
                  <stop offset="100%" stopColor={PALETTE.green}  stopOpacity={0.55} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f1f5" vertical={false} />
              <XAxis
                dataKey="name"
                stroke="#e8eaf0"
                tick={{ fill: PALETTE.midGray, fontSize: 12, fontWeight: 600 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                stroke="#e8eaf0"
                tick={{ fill: PALETTE.lightGray, fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `$${v.toFixed(2)}`}
              />
              <Tooltip cursor={{ fill: '#f4f6fb' }} content={<ChartTooltip />} />
              <ReferenceLine
                y={PRECIO_OFICIAL}
                stroke={PALETTE.lightGray}
                strokeDasharray="4 4"
                label={{
                  value: `Oficial ${fmtCurrency(PRECIO_OFICIAL)}`,
                  position: 'right',
                  fill: PALETTE.midGray,
                  fontSize: 10,
                }}
              />
              <Bar dataKey="value" radius={[10, 10, 0, 0]}>
                {chartData.map((d) => (
                  <Cell key={d.accent} fill={`url(#msGrad${d.accent[0].toUpperCase()}${d.accent.slice(1)})`} />
                ))}
                <LabelList
                  dataKey="value"
                  position="top"
                  formatter={(v) => fmtCurrency(v)}
                  style={{ fill: PALETTE.navy, fontSize: 13, fontWeight: 700 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {insight && (
          <div className="prc-insight">
            <span className="prc-insight__icon">{insight.icon}</span>
            <span className="prc-insight__text">{insight.text}</span>
          </div>
        )}
      </div>

      {/* 12-month historical mini-chart */}
      {history.length >= 2 && (
        <div className="prc-card">
          <div className="prc-section__row">
            <h3 className="prc-section__title">Últimos 12 meses</h3>
            {histStats && (
              <span className={`prc-delta ${histStats.diff >= 0 ? 'prc-delta--up' : 'prc-delta--down'}`}>
                {histStats.diff >= 0 ? '▲' : '▼'} {fmtPct(histStats.pct)} vs mes anterior
              </span>
            )}
          </div>
          <div className="prc-chart-wrap">
            <div className="prc-chart prc-chart--mini" ref={histChartContainerRef} />
            <div className="prc-tip" ref={histTooltipRef} />
          </div>
        </div>
      )}

      {/* Detail table */}
      <div className="prc-card">
        <h3 className="prc-section__title">Detalle del mes</h3>
        <table className="prc-table">
          <tbody>
            <tr>
              <td>Precio promedio por caja</td>
              <td className="prc-table__right prc-table__strong">{fmtCurrency(stats.avg)}</td>
            </tr>
            <tr>
              <td>Precio promedio por kg</td>
              <td className="prc-table__right">{fmtCurrency(stats.avgKg, 3)}</td>
            </tr>
            <tr>
              <td>Precio máximo</td>
              <td className="prc-table__right prc-table__up">{fmtCurrency(stats.max)}</td>
            </tr>
            <tr>
              <td>Precio mínimo</td>
              <td className="prc-table__right prc-table__down">{fmtCurrency(stats.min)}</td>
            </tr>
            <tr>
              <td>Precio oficial (referencia)</td>
              <td className="prc-table__right prc-table__muted">{fmtCurrency(PRECIO_OFICIAL)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MonthlySummary;
